import React, {
	ReactElement,
	createContext, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useSelector } from 'react-redux';
import useShots from '../shots/provider';
import { GreenroomState } from '../../stores/types/types';
import { VID_TYPES } from '../../types/constants';
import { ShotFeed } from '../../stores/reducers/shots';

interface VideoCacheContextType {
	// returns a video element
	// getElement: (mediaId: number, url: string) => ReactElement<any, any> | undefined;
	// returns a map of video elements keyed by their url
	// getUrlMap: (mediaId: number) => Map<string, ReactElement<any, any>> | undefined;
	// detaches all video elements from the dom
	detachElements: () => void;
	// detaches a single video element from the dom
	// detachSingleElement: (mediaId: number, url: string) => void;
	// a map of maps, <mediaId, <url, videoElement>>
	preloadedElements: null | React.MutableRefObject<Map<string, Map<number, (number|undefined)[]>>>;
}

export const VideoCacheContext = createContext<VideoCacheContextType>({
	// getElement: () => undefined,
	// getUrlMap: () => new Map(),
	detachElements: () => undefined,
	// detachSingleElement: () => undefined,
	preloadedElements: null,
});

interface Props {
	children: React.ReactNode;
}

const isUrlVideo = (url: string) => VID_TYPES.includes(url.split('.').pop() ?? '');

// const createVideoElement = (url: string, id: number) => {
// 	const videoElm = (<video
// 		key={`preloaded-video-${id}-${url}`}
// 		id={`preloaded-video-${id}-${url}`}
// 		autoPlay={false}
// 		muted
// 		src={url}
// 		style={{
// 			position: 'absolute', display: 'none', top: '0', left: '0',
// 		}}
// 	/>);

// 	return videoElm;
// };

// interface PreloadedRef{
// 	current:<number,(number|undefined)[]>;
// 	next: <number,(number|undefined)[]>;
// }
export const UseVideoCache = (): VideoCacheContextType => {
	const shots = useSelector((state: GreenroomState) => state.ShotsReducer.shots);
	const media = useSelector((state: GreenroomState) => state.MediaReducer.media);

	const { currentShot } = useShots();

	const preloadedElements = useRef(new Map<string, Map<number,(number|undefined)[]>>());

	// the next shot in the show
	const nextShot = useMemo(() => {
		const nextShotOrder = currentShot.shot_data.order + 1;
		const nNextShot = shots.find((shot) => shot.shot_data.order === nextShotOrder);
		return nNextShot;
	}, [currentShot, shots]);

	const detachElements = useCallback(() => {
		preloadedElements.current.forEach((urlMap) => {
			urlMap.forEach((videoElement) => {
				const { id } = videoElement as any;
				document.getElementById(id)?.remove();
			});
		});
	}, []);

	// const detachSingleElement = useCallback((mediaId: number, url: string) => {
	// 	const videoElement = preloadedElements.current.get(mediaId)?.get(url);
	// 	const { id } = videoElement as any ?? {};
	// 	if (id) { document.getElementById(id)?.remove(); }
	// }, []);

	// const createAndAttachVideos = useCallback((videoUrls: (number|undefined)[], mediaId: number) => {
	// 	const mediaIdExists = preloadedElements.current.get(mediaId);
	// 	if (!mediaIdExists) {
	// 		preloadedElements.current.set(mediaId, new Map());
	// 	}

	// 	if (videoUrls.length > 0) {
	// 		preloadedElements.current.get(mediaId)?.set('pages', videoUrls);
	// 	}
	// }, []);

	// used to re-render the hook and attached new videos to the dom in a useEffect
	useEffect(() => {
		if (!currentShot) { return; }

		const newUrlsByMediaId = new Map<string, Map<number, (number|undefined)[]>>();
		newUrlsByMediaId.set('current-shot', new Map());
		newUrlsByMediaId.set('next-shot', new Map());

		// const allFeeds = [...currentShot.shot_data.shotFeeds, ...nextShot?.shot_data.shotFeeds ?? []];
		currentShot.shot_data.shotFeeds.forEach((feed: ShotFeed) => {
			const { media: mediaId } = feed;
			if (mediaId || mediaId === 0) {
				console.log('mediaId', mediaId);
				const item = media.find((m) => m.id === mediaId);
				const videoIndices = item?.media_data.image_array.map((element, index) => {
					if (isUrlVideo(element)) {
						return index + 1;
					}
				}) ?? [1];
				newUrlsByMediaId.get('current-shot')?.set(mediaId, videoIndices);
			}
		});

		nextShot?.shot_data.shotFeeds.forEach((feed: ShotFeed) => {
			const { media: mediaId } = feed;
			if (mediaId || mediaId === 0) {
				const item = media.find((m) => m.id === mediaId);
				const videoIndices = item?.media_data.image_array.map((element, index) => {
					if (isUrlVideo(element)) {
						return index + 1;
					}
				}) ?? [1];
				newUrlsByMediaId.get('next-shot')?.set(mediaId, videoIndices);
			}
		});

		// map over all feeds and add non-duplicates to our new urlsByMediaId map
		// allFeeds.reduce((arr: Array<number>, feed: ShotFeed) => {
		// 	const { media: mediaId } = feed;
		// 	if (mediaId && !arr.includes(mediaId)) {
		// 		const item = media.find((m) => m.id === mediaId);

		// 		const videoIndices = item?.media_data.image_array.map((element, index) => {
		// 			if (isUrlVideo(element)) {
		// 				return index + 1;
		// 			}
		// 		}) ?? [];

		// 		if (videoIndices.length > 0 && mediaId) {
		// 			newUrlsByMediaId.get('current-shot')?.set(mediaId, videoIndices);
		// 		}

		// 		arr.push(mediaId);
		// 	}
		// 	return arr;
		// }, []);

		// whenever the shot changes, this will trigger
		// and update the preloadedElements map
		// newUrlsByMediaId.forEach(createAndAttachVideos);
		preloadedElements.current = newUrlsByMediaId;
	}, [currentShot.shot_data.shotFeeds, nextShot?.shot_data.shotFeeds]);

	return {
		// getElement: (id: number) => preloadedElements.current.get(id)?.get('pages'),
		// getUrlMap: (id: number) => preloadedElements.current?.get(id),
		detachElements,
		// detachSingleElement,
		preloadedElements,
	};
};

const VideoCacheProvider: React.FC<Props> = ({ children }: Props) => {
	const videoCacheContext = UseVideoCache();
	return <VideoCacheContext.Provider value={videoCacheContext}>
		{children}
	</VideoCacheContext.Provider>;
};

export default VideoCacheProvider;
